import { trpc } from './trpc';
import { useToast } from '@/components/ui/use-toast';

/**
 * Hooks for the Apple Pay BYOK registrations API.
 *
 * Two surfaces mirror the tRPC router:
 *  - `admin.*` — account-manager surface (cross-account), used by the
 *    account-manager Certificates tab.
 *  - top-level — merchant surface (account-scoped), used by the merchant
 *    self-serve page.
 *
 * This is separate from `apple-pay-domains.ts`, which manages the
 * platform-managed Apple Pay domain pool.
 */

// ---------------------------------------------------------------------------
// Account-manager surface (admin.*)
// ---------------------------------------------------------------------------

export function useAdminApplePayRegistrations(opts?: { enabled?: boolean }) {
  return trpc.applePayRegistrations.admin.listAll.useQuery(undefined, {
    staleTime: 30 * 1000,
    enabled: opts?.enabled ?? true,
  });
}

export function useAdminCreateApplePayRegistration() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.create.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      toast({
        title: 'Registration created',
        description: 'Apple Pay BYOK registration created at Basis Theory',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Create failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminUploadApplePayCertificates() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.uploadCertificates.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      toast({
        title: 'Certificates uploaded',
        description: 'Apple Pay certificates uploaded to Basis Theory',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminGenerateApplePayCsr() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.generateCsr.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
    },
    onError: (error: { message: string }) => {
      toast({
        title: 'Could not create signing request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useAdminUploadAppleCertificate() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.uploadAppleCertificate.useMutation({
    onSuccess: (result) => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      if (result?.finalized) {
        toast({ title: 'Certificates active', description: 'Both Apple certificates are set up' });
      } else {
        toast({ title: 'Certificate received', description: 'Upload the other certificate to finish' });
      }
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Certificate rejected', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminAddApplePayDomain() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.addDomain.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      toast({ title: 'Domain added', description: 'Verify it in the Apple Developer portal, then probe it' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Add domain failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminProbeApplePayDomain() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.probeDomain.useMutation({
    onSuccess: (result) => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      if (result?.matches) {
        toast({
          title: 'Domain verified',
          description: 'Association file matched — Apple Pay is now active for this domain',
        });
      } else {
        toast({
          title: 'Not verified yet',
          description:
            result?.error ??
            'The association file is not reachable or does not match. Serve it, verify in Apple, then retry.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Probe failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminApplePayAssociationFile(registrationId: string, opts?: { enabled?: boolean }) {
  return trpc.applePayRegistrations.admin.getAssociationFile.useQuery(
    { registrationId },
    { enabled: opts?.enabled ?? true, staleTime: 0 },
  );
}

export function useAdminUpdateApplePayAssociationFile() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.updateAssociationFile.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      utils.applePayRegistrations.admin.getAssociationFile.invalidate();
      toast({
        title: 'Association file saved',
        description: 'It is now served at every attached domain — re-verify/probe as needed',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminDomainAssociationFile(domainId: string, opts?: { enabled?: boolean }) {
  return trpc.applePayRegistrations.admin.getDomainAssociationFile.useQuery(
    { domainId },
    { enabled: opts?.enabled ?? true, staleTime: 0 },
  );
}

export function useAdminUpdateDomainAssociationFile() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.updateDomainAssociationFile.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      utils.applePayRegistrations.admin.getDomainAssociationFile.invalidate();
      toast({
        title: 'Association file saved',
        description: 'Served at this domain — probe it to verify',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminRemoveApplePayDomain() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.removeDomain.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      toast({ title: 'Domain removed' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Remove failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminDisableApplePayRegistration() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.disableRegistration.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      toast({ title: 'Registration disabled', description: 'Its domains no longer serve BYOK Apple Pay' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Disable failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminDeleteApplePayRegistration() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.admin.deleteRegistration.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.admin.listAll.invalidate();
      toast({ title: 'Registration deleted', description: 'Removed from Basis Theory and our system' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAdminCheckApplePayBtHealth() {
  const { toast } = useToast();
  return trpc.applePayRegistrations.admin.checkBtHealth.useMutation({
    onError: (error: { message: string }) => {
      toast({ title: 'Health check failed', description: error.message, variant: 'destructive' });
    },
  });
}

// ---------------------------------------------------------------------------
// Merchant surface (account-scoped, self-serve)
// ---------------------------------------------------------------------------

export function useMyApplePayRegistrations(opts?: { enabled?: boolean }) {
  return trpc.applePayRegistrations.list.useQuery(undefined, {
    staleTime: 30 * 1000,
    enabled: opts?.enabled ?? true,
  });
}

export function useCreateMyApplePayRegistration() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.create.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.list.invalidate();
      toast({ title: 'Registration created', description: 'Now upload your Apple certificates' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Create failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUploadMyApplePayCertificates() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.uploadCertificates.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.list.invalidate();
      toast({ title: 'Certificates uploaded', description: 'Your Apple Pay certificates are set up' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useGenerateMyApplePayCsr() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.generateCsr.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.list.invalidate();
    },
    onError: (error: { message: string }) => {
      toast({
        title: 'Could not create signing request',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUploadMyAppleCertificate() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.uploadAppleCertificate.useMutation({
    onSuccess: (result) => {
      utils.applePayRegistrations.list.invalidate();
      if (result?.finalized) {
        toast({ title: 'Certificates active', description: 'Your Apple Pay certificates are set up' });
      } else {
        toast({ title: 'Certificate received', description: 'Upload the other certificate to finish' });
      }
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Certificate rejected', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAddMyApplePayDomain() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.addDomain.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.list.invalidate();
      toast({
        title: 'Domain added',
        description: 'Verify it in the Apple Developer portal, then verify here',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Add domain failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useProbeMyApplePayDomain() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.probeDomain.useMutation({
    onSuccess: (result) => {
      utils.applePayRegistrations.list.invalidate();
      if (result?.matches) {
        toast({ title: 'Domain verified', description: 'Apple Pay is now active on this domain' });
      } else {
        toast({
          title: 'Not verified yet',
          description:
            result?.error ??
            'The association file is not reachable or does not match yet. Make sure it is served, then retry.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Verify failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useMyApplePayAssociationFile(registrationId: string, opts?: { enabled?: boolean }) {
  return trpc.applePayRegistrations.getAssociationFile.useQuery(
    { registrationId },
    { enabled: opts?.enabled ?? true, staleTime: 0 },
  );
}

export function useUpdateMyApplePayAssociationFile() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.updateAssociationFile.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.list.invalidate();
      utils.applePayRegistrations.getAssociationFile.invalidate();
      toast({
        title: 'Association file saved',
        description: 'It is now served on your domains — verify them in Apple, then Verify here',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useMyDomainAssociationFile(domainId: string, opts?: { enabled?: boolean }) {
  return trpc.applePayRegistrations.getDomainAssociationFile.useQuery(
    { domainId },
    { enabled: opts?.enabled ?? true, staleTime: 0 },
  );
}

export function useUpdateMyDomainAssociationFile() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.updateDomainAssociationFile.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.list.invalidate();
      utils.applePayRegistrations.getDomainAssociationFile.invalidate();
      toast({
        title: 'Association file saved',
        description: 'Served on this domain — verify it in Apple, then Probe here',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useRemoveMyApplePayDomain() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.removeDomain.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.list.invalidate();
      toast({ title: 'Domain removed' });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Remove failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useDisableMyApplePayRegistration() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.disableRegistration.useMutation({
    onSuccess: () => {
      utils.applePayRegistrations.list.invalidate();
      toast({
        title: 'Reverted to platform Apple Pay',
        description: 'Your custom certificates are no longer used',
      });
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    },
  });
}

// TAG-2036: self-serve platform (PPIID) enable — separate system from BYOK
// above, exposed from this file because it shares the merchant-facing router.
export interface EnableApplePayStepResult {
  step: 'add' | 'probe' | 'register' | 'activate';
  ok: boolean;
  message?: string;
}

export interface EnableApplePayResult {
  domain: { domain: string; [key: string]: unknown };
  steps: EnableApplePayStepResult[];
}

export function useSuggestPlatformApplePayDomain(storeId: string, opts?: { enabled?: boolean }) {
  return trpc.applePayRegistrations.suggestPlatformApplePayDomain.useQuery(
    { storeId },
    { enabled: (opts?.enabled ?? true) && !!storeId, staleTime: 30 * 1000 },
  );
}

export function useEnablePlatformApplePay() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.applePayRegistrations.enablePlatformApplePay.useMutation({
    onSuccess: (result) => {
      utils.applePayRegistrations.storeStatus.invalidate();
      const failedStep = result.steps.find((s) => !s.ok);
      if (failedStep) {
        toast({
          title: `Stopped at "${failedStep.step}"`,
          description: failedStep.message,
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Apple Pay enabled', description: result.domain.domain });
      }
    },
    onError: (error: { message: string }) => {
      toast({ title: 'Could not enable Apple Pay', description: error.message, variant: 'destructive' });
    },
  });
}
